import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, TablePagination, LinearProgress, Box,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WorkIcon from '@mui/icons-material/Work';
import apiService, { authFetch } from '../../services/apiServices';
import useConfirm from '../../hooks/useConfirm';

const API_URL = process.env.REACT_APP_API_URL || '';

function fmtDate(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return str; }
}

// Normalize a date value (ISO string or Date) to YYYY-MM-DD for <input type="date">
function toInputDate(str) {
  if (!str) return '';
  try { return new Date(str).toISOString().slice(0, 10); } catch { return ''; }
}

export default function WorkerProjectHistory({ workerId, onChanged, onDeleted }) {
  const [rows, setRows]           = useState([]);
  const [projects, setProjects]   = useState([]);
  const [loader, setLoader]       = useState(true);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editRow, setEditRow]     = useState(null);   // assignment being edited (or null)
  const [editForm, setEditForm]   = useState({ project_id: '', start_date: '', end_date: '', hours_per_day: 8 });
  const [saving, setSaving]       = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(() => {
    if (!workerId) { setLoader(false); return; }
    setLoader(true);
    authFetch(`${API_URL}/api/workers/${workerId}/project-assignments`)
      .then(r => r.json())
      .then(d => { setRows(d.data || []); setLoader(false); })
      .catch(() => setLoader(false));
  }, [workerId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiService().get('projects')
      .then(r => setProjects(r.data || []))
      .catch(() => {});
  }, []);

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Fshi caktimin', message: 'A jeni i sigurt që dëshironi ta fshini këtë caktim?', confirmLabel: 'Fshi' }))) return;
    await authFetch(`${API_URL}/api/workers/${workerId}/project-assignments/${id}`, {
      method: 'DELETE',
    });
    load();
    if (onDeleted) onDeleted();
    if (onChanged) onChanged();
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditError('');
    setEditForm({
      project_id:    row.project_id || '',
      start_date:    toInputDate(row.start_date),
      end_date:      toInputDate(row.end_date),
      hours_per_day: row.hours_per_day != null ? +row.hours_per_day : 8,
    });
  };

  const closeEdit = () => { if (!saving) { setEditRow(null); setEditError(''); } };

  const editChange = (field) => (e) => {
    setEditForm(f => ({ ...f, [field]: e.target.value }));
    setEditError('');
  };

  const handleUpdate = async () => {
    if (!editForm.project_id) return setEditError('Zgjidhni projektin');
    if (!editForm.start_date) return setEditError('Vendosni datën e fillimit');
    const hpd = +editForm.hours_per_day || 0;
    if (hpd <= 0 || hpd > 8) return setEditError('Orët/ditë duhet të jenë midis 0.5 dhe 8');

    setSaving(true);
    setEditError('');
    try {
      const resp = await authFetch(
        `${API_URL}/api/workers/${workerId}/project-assignments/${editRow.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id:    Number(editForm.project_id),
            start_date:    editForm.start_date,
            end_date:      editForm.end_date || null,
            hours_per_day: hpd,
          }),
        }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        setEditError(data.message || data.error || 'Gabim gjatë ruajtjes');
      } else {
        setEditRow(null);
        load();
        if (onChanged) onChanged();
      }
    } catch (e) {
      setEditError('Gabim i papritur: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!loader && rows.length === 0) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WorkIcon color="primary" fontSize="small" />
          <Typography variant="h6">Historiku i caktimeve të projektit</Typography>
        </Box>
        {loader && <LinearProgress sx={{ my: 2 }} />}
        {!loader && (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F0F9FF' }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Projekti</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Orë/ditë</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Data e fillimit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Data e mbarimit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Statusi</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{row.project_name || `#${row.project_id}`}</TableCell>
                        <TableCell align="center">{row.hours_per_day != null ? `${+row.hours_per_day}h` : '—'}</TableCell>
                        <TableCell>{fmtDate(row.start_date)}</TableCell>
                        <TableCell>{fmtDate(row.end_date)}</TableCell>
                        <TableCell>
                          {row.end_date
                            ? <Chip label="Përfunduar" size="small" color="default" />
                            : <Chip label="Aktiv" size="small" color="success" />}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edito">
                            <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Fshi">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
            />
          </>
        )}
      </CardContent>

      <Dialog open={!!editRow} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edito caktimin e projektit</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{editError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Projekti</InputLabel>
                <Select
                  value={editForm.project_id}
                  label="Projekti"
                  onChange={editChange('project_id')}
                >
                  <MenuItem value=""><em>— Zgjidhni —</em></MenuItem>
                  {projects.map(p => (
                    <MenuItem key={p.Id || p.id} value={p.Id || p.id}>
                      {p.Name || p.name || `#${p.Id || p.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Orë/ditë"
                InputLabelProps={{ shrink: true }}
                value={editForm.hours_per_day}
                onChange={editChange('hours_per_day')}
                inputProps={{ min: 0.5, max: 8, step: 0.5 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data e fillimit"
                InputLabelProps={{ shrink: true }}
                value={editForm.start_date}
                onChange={editChange('start_date')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data e mbarimit (opsionale)"
                InputLabelProps={{ shrink: true }}
                value={editForm.end_date}
                onChange={editChange('end_date')}
                inputProps={{ min: editForm.start_date || undefined }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>Anulo</Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Ruaj
          </Button>
        </DialogActions>
      </Dialog>
      {confirmUI}
    </Card>
  );
}
